import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ContrastContextValue = {
  highContrast: boolean;
  toggle: () => void;
  setHighContrast: (v: boolean) => void;
};

const ContrastContext = createContext<ContrastContextValue>({
  highContrast: false,
  toggle: () => {},
  setHighContrast: () => {},
});

const STORAGE_KEY = "trisphere-hc";

export function ContrastProvider({ children }: { children: ReactNode }) {
  const [highContrast, setHC] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (highContrast) {
      root.classList.add("hc");
    } else {
      root.classList.remove("hc");
    }
    try {
      localStorage.setItem(STORAGE_KEY, highContrast ? "1" : "0");
    } catch {
      // ignore
    }
  }, [highContrast]);

  const toggle = () => setHC((v) => !v);
  const setHighContrast = (v: boolean) => setHC(v);

  return (
    <ContrastContext.Provider value={{ highContrast, toggle, setHighContrast }}>
      {children}
    </ContrastContext.Provider>
  );
}

export function useContrast() {
  return useContext(ContrastContext);
}

// Shell theme tokens — used by the parent dashboard chrome
export type ShellTheme = {
  bg: string;
  bgLight: string;
  border: string;
  borderSolid: string;
  text: string;
  textMid: string;
  textMuted: string;
  accent: string;
  accentDark: string;
  card: string;
  glassBg: string;
  glassBorder: string;
  glassBlur: string;
  shadow: string;
  borderWidth: number;
  navActiveBg: string;
  navActiveText: string;
  navIdleBg: string;
  navIdleBorder: string;
  navIdleText: string;
  mobileNavBg: string;
  mobileActiveBg: string;
  bottomNavBg: string;
  bottomNavBorder: string;
  bottomNavActiveBg: string;
  bottomNavIdleColor: string;
  bottomNavActiveColor: string;
  badgeBg: string;
  logoBg: string;
  goldAccent: string;
};

export function useShellTheme(): ShellTheme {
  const { highContrast } = useContrast();

  if (highContrast) {
    return {
      bg: "#FFFFFF",
      bgLight: "#F5F5F5",
      border: "#000000",
      borderSolid: "2px solid #000000",
      text: "#000000",
      textMid: "#1A1A1A",
      textMuted: "#333333",
      accent: "#8B0000",
      accentDark: "#6B0000",
      card: "#FFFFFF",
      glassBg: "#FFFFFF",
      glassBorder: "#000000",
      glassBlur: "none",
      shadow: "none",
      borderWidth: 2,
      navActiveBg: "#000000",
      navActiveText: "#FFFFFF",
      navIdleBg: "transparent",
      navIdleBorder: "#000000",
      navIdleText: "#000000",
      mobileNavBg: "#FFFFFF",
      mobileActiveBg: "#000000",
      bottomNavBg: "#FFFFFF",
      bottomNavBorder: "#000000",
      bottomNavActiveBg: "#000000",
      bottomNavIdleColor: "#000000",
      bottomNavActiveColor: "#FFFFFF",
      badgeBg: "#000000",
      logoBg: "#000000",
      goldAccent: "#B8860B",
    };
  }

  return {
    bg: "#F5EFE4",
    bgLight: "#EDE4D4",
    border: "#E2D6C0",
    borderSolid: "1px solid #E2D6C0",
    text: "#1C1917",
    textMid: "#3C3530",
    textMuted: "#8B6F47",
    accent: "#7B1416",
    accentDark: "#5C0F11",
    card: "#FFFFFF",
    glassBg: "rgba(245,239,228,0.85)",
    glassBorder: "rgba(28,25,23,0.10)",
    glassBlur: "blur(48px) saturate(180%)",
    shadow: "0 2px 8px rgba(0,0,0,0.04)",
    borderWidth: 1,
    navActiveBg: "#1C1917",
    navActiveText: "#fff",
    navIdleBg: "transparent",
    navIdleBorder: "#E2D6C0",
    navIdleText: "#3C3530",
    mobileNavBg: "rgba(245,239,228,0.90)",
    mobileActiveBg: "#1C1917",
    bottomNavBg: "rgba(255,255,255,0.82)",
    bottomNavBorder: "rgba(0,0,0,0.06)",
    bottomNavActiveBg: "#1C1917",
    bottomNavIdleColor: "#8B6F47",
    bottomNavActiveColor: "#fff",
    badgeBg: "#7B1416",
    logoBg: "#7B1416",
    goldAccent: "#C4A882",
  };
}
